import { useProfile, type Gender } from "@/contexts/ProfileContext";
import { useContestEntry } from "@/hooks/useContestEntry";
import { useWheelSpin } from "@/hooks/useWheelSpin";
import { useUserScores } from "@/hooks/useUserScores";
import { isLuckyWheelOpen } from "@/constants/challenge";
import { pickWheelOutcome } from "@/utils/wheel";
import { Ionicons } from "@expo/vector-icons";
import * as Haptics from "expo-haptics";
import { LinearGradient } from "expo-linear-gradient";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Animated,
  Easing,
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import ConfettiBurst from '@/components/ConfettiBurst';
import Svg, { Circle, G, Path, Text as SvgText } from "react-native-svg";

// 6 segments alternant cadeau et points. Cadeau → qualification au tirage
// (formulaire). Points → crédités directement, sans formulaire.
type Segment = { kind: "gift" } | { kind: "points"; value: number };
const SEGMENTS: Segment[] = [
  { kind: "gift" },
  { kind: "points", value: 10 },
  { kind: "gift" },
  { kind: "points", value: 20 },
  { kind: "gift" },
  { kind: "points", value: 30 },
];
const SEGMENT_COLORS = ["#1C453F", "#78A584", "#CD7728", "#1C453F", "#78A584", "#CD7728"];
const SEGMENT_COUNT = SEGMENTS.length;

type Step = "ready" | "spinning" | "form" | "submitting" | "done" | "wonPoints" | "spentToday";

interface LuckyWheelModalProps {
  visible: boolean;
  onClose: () => void;
}

export default function LuckyWheelModal({ visible, onClose }: LuckyWheelModalProps) {
  const { t } = useTranslation();
  const { hasEntered, hasEverEntered, entry, loading, submitEntry, refetch } = useContestEntry();
  const { profile, updateProfile } = useProfile();
  const { addPoints } = useUserScores();
  const { spunToday, hasEverSpun, loading: spinLoading, recordSpin, refetch: refetchSpin } = useWheelSpin();

  const spinValue = useRef(new Animated.Value(0)).current;

  const [step, setStep] = useState<Step>("ready");
  const [pointsWon, setPointsWon] = useState(0);
  const [email, setEmail] = useState("");
  const [ageInput, setAgeInput] = useState("");
  const [gender, setGender] = useState<Gender | "">("");
  const [formError, setFormError] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  // Le formulaire ne demande QUE l'âge, le genre et l'email : l'identification
  // du gagnant se fait par l'email du compte (demande client 08.2026 — nom et
  // prénom retirés). Ils restent modifiables depuis « Modifier le profil ».
  const needsAge = profile?.age == null;
  const needsGender = !profile?.gender;

  useEffect(() => {
    if (!visible) {
      // Reset internal flow each time the modal is closed
      setStep("ready");
      setEmail("");
      setAgeInput("");
      setGender("");
      setFormError(null);
      setShowConfetti(false);
      setPointsWon(0);
      spinValue.setValue(0);
      return;
    }
    // Pre-fill from profile — fields the user already filled aren't asked again.
    setEmail(profile?.email ?? "");
    setAgeInput(profile?.age != null ? String(profile.age) : "");
    setGender(profile?.gender ?? "");
    refetch();
    refetchSpin();
  }, [visible, refetch, refetchSpin, spinValue, profile?.email, profile?.age, profile?.gender]);

  useEffect(() => {
    if (!visible || step !== "ready" || loading || spinLoading) return;
    // Déjà qualifié au tirage → écran de confirmation. Sinon, déjà tourné
    // aujourd'hui (a gagné des points) → écran "reviens demain".
    if (hasEntered) setStep("done");
    else if (spunToday) setStep("spentToday");
  }, [visible, hasEntered, spunToday, step, loading, spinLoading]);

  const segmentAngle = 360 / SEGMENT_COUNT;

  const startSpin = () => {
    // Attendre la fin des checks serveur : hasEverSpun/hasEverEntered doivent
    // être fiables pour appliquer le « 1ᵉʳ tour = cadeau garanti ».
    if (!isLuckyWheelOpen() || step !== "ready" || loading || spinLoading) return;
    setStep("spinning");

    // 1) Résultat logique : 1ᵉʳ tour de l'utilisateur (aucun wheel_spins ni
    //    contest_entries) → cadeau garanti ; sinon 40 % cadeau / 60 % points.
    const isFirstSpin = !hasEverSpun && !hasEverEntered;
    const outcome = pickWheelOutcome(isFirstSpin);

    // 2) Segment d'atterrissage : choisi au hasard PARMI les segments du bon
    //    type, pour que l'aiguille soit cohérente avec le résultat.
    const candidateIndexes = SEGMENTS
      .map((segment, index) => ({ segment, index }))
      .filter(({ segment }) => segment.kind === outcome)
      .map(({ index }) => index);
    const targetSegment = candidateIndexes[Math.floor(Math.random() * candidateIndexes.length)];
    const extraSpins = 5;
    const targetAngle = segmentAngle * targetSegment + segmentAngle / 2;
    const totalRotation = extraSpins * 360 + (360 - targetAngle);

    const hapticInterval = setInterval(() => {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }, 100);

    spinValue.setValue(0);
    Animated.timing(spinValue, {
      toValue: totalRotation,
      duration: 3500,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start(async () => {
      clearInterval(hapticInterval);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);

      const segment = SEGMENTS[targetSegment];
      if (segment.kind === "points") {
        // Consomme le tour du jour (verrou serveur) puis crédite les points.
        const res = await recordSpin(segment.value);
        if (res.success && !res.alreadySpun) {
          await addPoints(segment.value, false);
          setPointsWon(segment.value);
          setShowConfetti(true);
          setStep("wonPoints");
        } else {
          // Déjà tourné aujourd'hui (ou échec) → pas de double crédit.
          setStep("spentToday");
        }
      } else {
        // Cadeau : qualification au tirage. Le tour n'est pas consommé tant
        // que la participation n'est pas confirmée — l'utilisateur peut
        // refermer et retenter (le tirage reste limité à 1/jour côté DB).
        setShowConfetti(true);
        setStep("form");
      }
    });
  };

  const handleSubmit = async () => {
    setFormError(null);

    // Validate missing-profile fields before submitting. Each is only required
    // if the profile doesn't already have it.
    if (needsAge) {
      const parsedAge = parseInt(ageInput, 10);
      if (!Number.isFinite(parsedAge) || parsedAge < 13 || parsedAge > 120) {
        setFormError(t("wheel.errors.ageRange"));
        return;
      }
    }
    if (needsGender && !gender) {
      setFormError(t("wheel.errors.genderRequired"));
      return;
    }

    setStep("submitting");

    // Persist any newly-filled profile fields BEFORE creating the contest
    // entry. If this fails we abort the entry so we don't end up with a
    // qualified user whose contact info is incomplete.
    if (needsAge || needsGender) {
      const profileUpdates: any = {};
      if (needsAge) profileUpdates.age = parseInt(ageInput, 10);
      if (needsGender) profileUpdates.gender = gender as Gender;
      const upd = await updateProfile(profileUpdates);
      if (!upd.success) {
        setStep("form");
        setFormError(t("wheel.errors.profileSaveFailed"));
        return;
      }
    }

    // Consent for prize-draw contact is given via T&C at signup — no
    // second checkbox here.
    const result = await submitEntry(email, true);
    if (result.success) {
      setStep("done");
      return;
    }
    setStep("form");
    switch (result.error) {
      case "invalid_email":
        setFormError(t("wheel.errors.invalidEmail"));
        break;
      case "consent_required":
        setFormError(t("wheel.errors.consentRequired"));
        break;
      case "already_entered":
        setFormError(t("wheel.errors.alreadyEntered"));
        setStep("done");
        break;
      case "not_authenticated":
        setFormError(t("wheel.errors.notAuthenticated"));
        break;
      default:
        setFormError(t("wheel.errors.generic"));
    }
  };

  const rotation = spinValue.interpolate({
    inputRange: [0, 360],
    outputRange: ["0deg", "360deg"],
  });

  const size = 260;
  const center = size / 2;
  const radius = center - 12;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        style={styles.overlay}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.container}>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onClose}
              hitSlop={12}
              accessibilityRole="button"
              accessibilityLabel={t("common.close")}
            >
              <Ionicons name="close" size={24} color="#333" />
            </TouchableOpacity>

            {!isLuckyWheelOpen() ? (
              <LockedView onClose={onClose} />
            ) : step === "ready" || step === "spinning" ? (
              <ReadyView
                size={size}
                center={center}
                radius={radius}
                segmentAngle={segmentAngle}
                rotation={rotation}
                onSpin={startSpin}
                isSpinning={step === "spinning"}
              />
            ) : step === "form" || step === "submitting" ? (
              <FormView
                email={email}
                onChangeEmail={setEmail}
                ageInput={ageInput}
                onChangeAge={setAgeInput}
                gender={gender}
                onChangeGender={setGender}
                showAge={needsAge}
                showGender={needsGender}
                onSubmit={handleSubmit}
                submitting={step === "submitting"}
                error={formError}
              />
            ) : step === "wonPoints" ? (
              <WonPointsView points={pointsWon} onClose={onClose} />
            ) : step === "spentToday" ? (
              <SpentTodayView onClose={onClose} />
            ) : (
              <DoneView email={entry?.email ?? email} onClose={onClose} />
            )}

            <Text style={styles.disclaimer}>{t("wheel.disclaimer")}</Text>
          </View>
        </ScrollView>

        {showConfetti && <ConfettiBurst />}
      </KeyboardAvoidingView>
    </Modal>
  );
}

// ----------------------------------------------------------------------------
// Subviews
// ----------------------------------------------------------------------------

interface ReadyViewProps {
  size: number;
  center: number;
  radius: number;
  segmentAngle: number;
  rotation: Animated.AnimatedInterpolation<string>;
  onSpin: () => void;
  isSpinning: boolean;
}

function ReadyView({ size, center, radius, segmentAngle, rotation, onSpin, isSpinning }: ReadyViewProps) {
  const { t } = useTranslation();
  return (
    <>
      <Text style={styles.title}>{t("wheel.title")}</Text>
      <Text style={styles.subtitle}>{t("wheel.subtitle")}</Text>

      <View style={[styles.wheelContainer, { width: size, height: size }]}>
        <View style={styles.pointer}>
          <Ionicons name="caret-down" size={36} color="#CD7728" />
        </View>

        <Animated.View style={{ transform: [{ rotate: rotation }] }}>
          <Svg width={size} height={size}>
            <G>
              {SEGMENT_COLORS.map((color, index) => {
                const startAngle = (index * segmentAngle - 90) * (Math.PI / 180);
                const endAngle = ((index + 1) * segmentAngle - 90) * (Math.PI / 180);
                const x1 = center + radius * Math.cos(startAngle);
                const y1 = center + radius * Math.sin(startAngle);
                const x2 = center + radius * Math.cos(endAngle);
                const y2 = center + radius * Math.sin(endAngle);
                const largeArc = segmentAngle > 180 ? 1 : 0;
                const pathData = `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

                const labelAngle = (index * segmentAngle + segmentAngle / 2 - 90) * (Math.PI / 180);
                const labelRadius = radius * 0.65;
                const labelX = center + labelRadius * Math.cos(labelAngle);
                const labelY = center + labelRadius * Math.sin(labelAngle);
                const segment = SEGMENTS[index];

                return (
                  <G key={index}>
                    <Path d={pathData} fill={color} stroke="#FFF" strokeWidth={3} />
                    {segment.kind === "gift" ? (
                      <SvgText
                        x={labelX}
                        y={labelY}
                        fill="#FFF"
                        fontSize={22}
                        fontWeight="bold"
                        textAnchor="middle"
                        alignmentBaseline="central"
                      >
                        🎁
                      </SvgText>
                    ) : (
                      <>
                        <SvgText
                          x={labelX}
                          y={labelY - 6}
                          fill="#FFF"
                          fontSize={20}
                          fontFamily="Inter_700Bold"
                          textAnchor="middle"
                          alignmentBaseline="central"
                        >
                          {`+${segment.value}`}
                        </SvgText>
                        <SvgText
                          x={labelX}
                          y={labelY + 12}
                          fill="#FFF"
                          fontSize={11}
                          fontFamily="Inter_700Bold"
                          textAnchor="middle"
                          alignmentBaseline="central"
                        >
                          {t("wheel.pointsUnit")}
                        </SvgText>
                      </>
                    )}
                  </G>
                );
              })}
              <Circle cx={center} cy={center} r={34} fill="#FFF" />
              <Circle cx={center} cy={center} r={28} fill="#FBEFE3" />
            </G>
          </Svg>
        </Animated.View>
      </View>

      <TouchableOpacity
        style={styles.spinButton}
        onPress={onSpin}
        disabled={isSpinning}
        activeOpacity={0.85}
      >
        <LinearGradient
          colors={["#CD7728", "#FCC189"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={[styles.spinGradient, isSpinning && styles.spinButtonDisabled]}
        >
          <Text style={styles.spinText}>{isSpinning ? t("wheel.spinning") : t("wheel.spinCta")}</Text>
        </LinearGradient>
      </TouchableOpacity>
    </>
  );
}

interface FormViewProps {
  email: string;
  onChangeEmail: (v: string) => void;
  ageInput: string;
  onChangeAge: (v: string) => void;
  gender: Gender | "";
  onChangeGender: (v: Gender) => void;
  showAge: boolean;
  showGender: boolean;
  onSubmit: () => void;
  submitting: boolean;
  error: string | null;
}

const GENDER_OPTIONS: { value: Gender; labelKey: string }[] = [
  { value: "femme", labelKey: "wheel.gender.femme" },
  { value: "homme", labelKey: "wheel.gender.homme" },
  { value: "autre", labelKey: "wheel.gender.autre" },
  { value: "prefere_ne_pas_dire", labelKey: "wheel.gender.prefereNePasDire" },
];

function FormView({
  email,
  onChangeEmail,
  ageInput,
  onChangeAge,
  gender,
  onChangeGender,
  showAge,
  showGender,
  onSubmit,
  submitting,
  error,
}: FormViewProps) {
  const { t } = useTranslation();
  const needsExtraInfo = showAge || showGender;
  return (
    <>
      <Text
        style={styles.title}
        accessibilityRole="header"
        accessibilityLabel={t("wheel.wonTitleA11y")}
      >
        {t("wheel.wonTitle")}
      </Text>
      <Text style={styles.subtitle}>
        {needsExtraInfo
          ? t("wheel.formSubtitleExtraInfo")
          : t("wheel.formSubtitleConfirmEmail")}
      </Text>

      {showAge && (
        <View style={styles.formGroup}>
          <Text style={styles.label}>{t("wheel.ageLabel")}</Text>
          <TextInput
            style={styles.input}
            value={ageInput}
            onChangeText={(v) => onChangeAge(v.replace(/[^0-9]/g, "").slice(0, 3))}
            placeholder={t("wheel.agePlaceholder")}
            placeholderTextColor="#8A8A8A"
            keyboardType="number-pad"
            inputMode="numeric"
            editable={!submitting}
            maxLength={3}
          />
        </View>
      )}

      {showGender && (
        <View style={styles.formGroup}>
          <Text style={styles.label}>{t("wheel.genderLabel")}</Text>
          <View style={styles.genderRow}>
            {GENDER_OPTIONS.map((opt) => {
              const selected = gender === opt.value;
              return (
                <TouchableOpacity
                  key={opt.value}
                  style={[styles.genderChip, selected && styles.genderChipSelected]}
                  onPress={() => onChangeGender(opt.value)}
                  disabled={submitting}
                  accessibilityRole="radio"
                  accessibilityState={{ selected }}
                >
                  <Text
                    style={[
                      styles.genderChipText,
                      selected && styles.genderChipTextSelected,
                    ]}
                  >
                    {t(opt.labelKey)}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      )}

      <View style={styles.formGroup}>
        <Text style={styles.label} nativeID="contest-email-label">
          {t("wheel.emailLabel")}
        </Text>
        <TextInput
          style={styles.input}
          value={email}
          onChangeText={onChangeEmail}
          placeholder={t("wheel.emailPlaceholder")}
          placeholderTextColor="#8A8A8A"
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          autoComplete="email"
          textContentType="emailAddress"
          inputMode="email"
          returnKeyType="done"
          editable={!submitting}
          accessibilityLabel={t("wheel.emailA11y")}
          accessibilityLabelledBy="contest-email-label"
          maxLength={254}
        />
      </View>

      {error ? (
        <Text
          style={styles.errorText}
          accessibilityLiveRegion="assertive"
          accessibilityRole="alert"
        >
          {error}
        </Text>
      ) : null}

      <TouchableOpacity
        style={styles.spinButton}
        onPress={onSubmit}
        disabled={submitting}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel={t("wheel.submitCta")}
        accessibilityState={{ disabled: submitting, busy: submitting }}
      >
        <LinearGradient
          colors={["#1C453F", "#78A584"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={[styles.spinGradient, submitting && styles.spinButtonDisabled]}
        >
          {submitting ? (
            <ActivityIndicator color="#FFF" />
          ) : (
            <Text style={styles.spinText}>{t("wheel.submitCta")}</Text>
          )}
        </LinearGradient>
      </TouchableOpacity>
    </>
  );
}

interface DoneViewProps {
  email: string;
  onClose: () => void;
}

function DoneView({ email, onClose }: DoneViewProps) {
  const { t } = useTranslation();
  return (
    <>
      <Text style={styles.title} accessibilityRole="header">
        {t("wheel.doneTitle")}
      </Text>
      <Text style={styles.subtitle}>
        {t("wheel.doneSubtitlePrefix")}{" "}
        <Text style={styles.emailStrong}>{email || t("wheel.accountEmailFallback")}</Text>.
      </Text>

      <TouchableOpacity
        style={styles.spinButton}
        onPress={onClose}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel={t("wheel.closeA11y")}
      >
        <LinearGradient
          colors={["#1C453F", "#78A584"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={styles.spinGradient}
        >
          <Text style={styles.spinText}>{t("common.close")}</Text>
        </LinearGradient>
      </TouchableOpacity>
    </>
  );
}

function WonPointsView({ points, onClose }: { points: number; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <>
      <Text style={styles.title} accessibilityRole="header">
        {t("wheel.wonTitle")}
      </Text>
      <View style={styles.welcomeBonusPill}>
        <Text style={styles.welcomeBonusText}>{t("wheel.wonPointsPill", { points })}</Text>
      </View>
      <Text style={styles.subtitle}>{t("wheel.wonPointsSubtitle", { points })}</Text>

      <TouchableOpacity
        style={styles.spinButton}
        onPress={onClose}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel={t("wheel.closeA11y")}
      >
        <LinearGradient
          colors={["#1C453F", "#78A584"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={styles.spinGradient}
        >
          <Text style={styles.spinText}>{t("wheel.wonPointsCta")}</Text>
        </LinearGradient>
      </TouchableOpacity>
    </>
  );
}

function LockedView({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <>
      <Text style={styles.title} accessibilityRole="header">
        {t("wheel.lockedTitle")}
      </Text>
      <Text style={styles.subtitle}>{t("wheel.lockedSubtitle")}</Text>

      <TouchableOpacity
        style={styles.spinButton}
        onPress={onClose}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel={t("wheel.lockedCta")}
      >
        <LinearGradient
          colors={["#1C453F", "#78A584"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={styles.spinGradient}
        >
          <Text style={styles.spinText}>{t("wheel.lockedCta")}</Text>
        </LinearGradient>
      </TouchableOpacity>
    </>
  );
}

function SpentTodayView({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <>
      <Text style={styles.title} accessibilityRole="header">
        {t("wheel.spentTitle")}
      </Text>
      <Text style={styles.subtitle}>{t("wheel.spentSubtitle")}</Text>

      <TouchableOpacity
        style={styles.spinButton}
        onPress={onClose}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel={t("wheel.closeA11y")}
      >
        <LinearGradient
          colors={["#1C453F", "#78A584"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={styles.spinGradient}
        >
          <Text style={styles.spinText}>{t("common.close")}</Text>
        </LinearGradient>
      </TouchableOpacity>
    </>
  );
}

// ----------------------------------------------------------------------------
// Styles
// ----------------------------------------------------------------------------

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  container: {
    backgroundColor: "#FFF",
    borderRadius: 24,
    padding: 24,
    alignItems: "center",
    width: "100%",
    maxWidth: 360,
  },
  closeButton: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 44,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
  },
  title: {
    fontFamily: "Inter_700Bold",
    fontSize: 22,
    color: "#000",
    marginTop: 12,
    marginBottom: 8,
    textAlign: "center",
  },
  subtitle: {
    fontFamily: "Inter_400Regular",
    fontSize: 15,
    color: "#444",
    textAlign: "center",
    marginBottom: 20,
    lineHeight: 22,
  },
  wheelContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  pointer: {
    position: "absolute",
    top: -8,
    zIndex: 10,
  },
  spinButton: {
    width: "100%",
  },
  spinButtonDisabled: {
    opacity: 0.7,
  },
  spinGradient: {
    minHeight: 52,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  spinText: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#FFF",
  },
  disclaimer: {
    fontFamily: "Inter_400Regular",
    fontSize: 11,
    color: "#666",
    textAlign: "center",
    marginTop: 16,
    lineHeight: 16,
  },
  formGroup: {
    width: "100%",
    marginBottom: 14,
  },
  genderRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  genderChip: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: "#BBB",
    backgroundColor: "#FFF",
  },
  genderChipSelected: {
    backgroundColor: "#1C453F",
    borderColor: "#1C453F",
  },
  genderChipText: {
    fontFamily: "Inter_400Regular",
    fontSize: 13,
    color: "#333",
  },
  genderChipTextSelected: {
    color: "#FFF",
    fontFamily: "Inter_700Bold",
  },
  label: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 14,
    color: "#222",
    marginBottom: 8,
  },
  input: {
    fontFamily: "Inter_400Regular",
    fontSize: 17,
    color: "#000",
    borderWidth: 1.5,
    borderColor: "#BBB",
    borderRadius: 12,
    paddingHorizontal: 14,
    minHeight: 52,
    paddingVertical: 14,
    backgroundColor: "#FFF",
  },
  errorText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 14,
    color: "#B0291C",
    marginBottom: 10,
    textAlign: "center",
  },
  welcomeBonusPill: {
    backgroundColor: "#FFF4E6",
    borderColor: "#FCC189",
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 8,
    marginBottom: 12,
    alignSelf: "center",
  },
  welcomeBonusText: {
    fontFamily: "Inter_700Bold",
    fontSize: 14,
    color: "#CD7728",
  },
  emailStrong: {
    fontFamily: "Inter_600SemiBold",
    color: "#1C453F",
  },
});
